import {
  areDatesEqual,
  areTimesEqual,
  toValidDate,
  toValidTime,
} from "../ruleEditorTimeUtils";
import type {
  BulkCategoryMode,
  BulkChangeModes,
  BulkEffectiveMode,
  BulkSingletonMode,
} from "../liftAccessRuleSet/LiftAccessRuleSetBulkEditor";
import { AccessRuleChangeSet, AccessRuleEntitlementContext } from "../models";
import { LiftAccessRuleSet } from "../liftAccessRuleSet/models";
import { AccessRuleChangeRequest } from "./accessRuleRequestBuilder";
import { getRequestUtcDateTime } from "./accessRuleRequestMapper";
import {
  mapBlackoutDateRange,
  mapBlackoutDayOfWeek,
  mapEffectiveDateRange,
  mapEffectiveDateRangeUpdate,
  mapMaxDays,
  mapMaxDaysUpdate,
} from "./accessRuleRuleMappers";
import { blackoutDateRangeDiffAdapter } from "../blackoutDateRange/blackoutDateRange";
import { blackoutDayOfWeekDiffAdapter } from "../blackoutDayOfWeek/blackoutDayOfWeek";

/**
 * What a bulk edit would really write to one rule set. The review scan calls these
 * rather than reimplementing them, so a preview can't claim a change the request
 * won't carry.
 */

/**
 * The rules an "add" would really create: those the set doesn't already have, by
 * identity key. Without it, adding a blackout the set carries writes a duplicate.
 */
export function rulesToAdd<TDefined, TExisting>(
  definedRules: TDefined[],
  existingRules: TExisting[] | undefined,
  getIdentityKey: (rule: TDefined | TExisting) => string,
): TDefined[] {
  const seen = new Set((existingRules ?? []).map(getIdentityKey));
  return definedRules.filter((rule) => {
    const key = getIdentityKey(rule);
    if (seen.has(key)) return false;
    seen.add(key); // also collapses duplicates within the defined set
    return true;
  });
}

/** A complete effective rule: all four fields present, ready to map to a payload. */
export type MergedEffectiveRule = {
  effectiveDateStart: Date;
  effectiveDateEnd: Date;
  effectiveTimeSlotStart: Date;
  effectiveTimeSlotEnd: Date;
};

/**
 * The effective rule a bulk edit would leave on one rule set, or null to leave it
 * alone. Dates and time replace independently, so an "unchanged" part comes from the
 * set's own rule — and can come up short if that part is missing or unparseable.
 * Returns null rather than a partial rule: the mappers format null as "", which the
 * API reads as an empty date.
 */
export function mergeEffectiveRule(
  primary: any,
  defined: any,
  dateMode: BulkEffectiveMode,
  timeMode: BulkEffectiveMode,
): MergedEffectiveRule | null {
  const dateReplace = dateMode === "replace";
  const timeReplace = timeMode === "replace";

  if (!dateReplace && !timeReplace) return null;
  if (!defined) return null;

  // Field by field, not toEditableEffectiveDateRange — that helper mints a UUID, and
  // this runs per rule set per effective column in the review scan.
  const base = primary
    ? {
        effectiveDateStart: toValidDate(primary.effectiveDateStart),
        effectiveDateEnd: toValidDate(primary.effectiveDateEnd),
        effectiveTimeSlotStart: toValidTime(primary.effectiveTimeSlotStart),
        effectiveTimeSlotEnd: toValidTime(primary.effectiveTimeSlotEnd),
      }
    : null;

  const merged = {
    effectiveDateStart: dateReplace
      ? defined.effectiveDateStart
      : base?.effectiveDateStart ?? null,
    effectiveDateEnd: dateReplace
      ? defined.effectiveDateEnd
      : base?.effectiveDateEnd ?? null,
    effectiveTimeSlotStart: timeReplace
      ? defined.effectiveTimeSlotStart
      : base?.effectiveTimeSlotStart ?? null,
    effectiveTimeSlotEnd: timeReplace
      ? defined.effectiveTimeSlotEnd
      : base?.effectiveTimeSlotEnd ?? null,
  };

  return merged.effectiveDateStart &&
    merged.effectiveDateEnd &&
    merged.effectiveTimeSlotStart &&
    merged.effectiveTimeSlotEnd
    ? (merged as MergedEffectiveRule)
    : null;
}

export type MapAccessRuleBulkChangeParams = {
  userId: string;
  operatorId: string;
  ruleSets: LiftAccessRuleSet[];
  /** The rules defined in the bulk editor - all live in the `created` bucket. */
  changeSet: AccessRuleChangeSet;
  modes: BulkChangeModes;
};

// A factory, not a shared constant: the returned object (and its arrays) ends up
// inside every request, so one mutation would corrupt them all.
const emptyPayload = () => ({ add: [], update: [], delete: [] });

function toContext(ruleSet: LiftAccessRuleSet): AccessRuleEntitlementContext {
  return {
    EntitlementTypeId: ruleSet.entitlementTypeId,
    FulfillmentSeasonId: ruleSet.fulfillmentSeasonId,
    LiftAccessRuleScopeCategory: ruleSet.scopeCategoryId,
    LiftAccessRuleScopeEntityId: ruleSet.locationId,
  };
}

function collectDeleteIds(rules: { liftAccessRuleId: number }[] | undefined) {
  return (rules ?? [])
    .map((r) => r.liftAccessRuleId)
    .filter((id): id is number => id != null);
}

/**
 * Builds the add/update/delete payload for a list-style category
 * (blackout date range, blackout day of week).
 */
function buildListCategory<
  TDefined,
  TExisting extends { liftAccessRuleId: number },
>(
  mode: BulkCategoryMode,
  definedRules: TDefined[],
  mapCreate: (rule: TDefined) => any,
  existingRules: TExisting[] | undefined,
  getIdentityKey: (rule: TDefined | TExisting) => string,
) {
  if (mode === "unchanged") {
    return emptyPayload();
  }

  if (mode === "clear") {
    return { add: [], update: [], delete: collectDeleteIds(existingRules) };
  }

  // Replace deletes everything first, so nothing is left to duplicate.
  const toAdd =
    mode === "replace"
      ? definedRules
      : rulesToAdd(definedRules, existingRules, getIdentityKey);

  return {
    add: toAdd.map(mapCreate),
    update: [],
    delete: mode === "replace" ? collectDeleteIds(existingRules) : [],
  };
}

/**
 * Builds the add/update/delete payload for a singleton category (max days). A rule
 * set holds at most one, so "replace" updates the existing rule in place (or adds
 * one if none exists) and deletes any legacy extras so exactly one remains.
 */
function buildSingletonCategory<TExisting extends { liftAccessRuleId: number }>(
  mode: BulkSingletonMode,
  definedRules: any[],
  mapCreate: (rule: any) => any,
  mapUpdate: (rule: any) => any,
  existingRules: TExisting[] | undefined,
  /** True when the set's rule already holds the defined value (no update needed). */
  matchesExisting: (defined: any, existing: TExisting) => boolean,
) {
  if (mode === "unchanged") {
    return emptyPayload();
  }

  // "clear" removes the singleton (and any legacy extras) entirely.
  if (mode === "clear") {
    return { add: [], update: [], delete: collectDeleteIds(existingRules) };
  }

  if (definedRules.length === 0) {
    return emptyPayload();
  }

  const defined = definedRules[0];
  const existing = existingRules ?? [];
  const primary = existing[0];
  // Any rules beyond the first are legacy extras and get removed on replace.
  const extraDeleteIds = collectDeleteIds(existing.slice(1));

  if (primary?.liftAccessRuleId != null) {
    // Nothing to update when the set already holds this value; only the legacy
    // extras (if any) still need removing.
    if (matchesExisting(defined, primary)) {
      return { add: [], update: [], delete: extraDeleteIds };
    }

    return {
      add: [],
      update: [
        mapUpdate({ ...defined, liftAccessRuleId: primary.liftAccessRuleId }),
      ],
      delete: extraDeleteIds,
    };
  }

  return {
    add: [mapCreate(defined)],
    update: [],
    delete: extraDeleteIds,
  };
}

/**
 * Builds the effective-date-range payload. Applied as an in-place update on the
 * set's existing rule, or an add when it has none — in both cases only when
 * mergeEffectiveRule yields a complete rule.
 */
function buildEffectiveCategory(
  dateMode: BulkEffectiveMode,
  timeMode: BulkEffectiveMode,
  definedRules: any[],
  existingRules: { liftAccessRuleId: number }[] | undefined,
) {
  const existing = existingRules ?? [];
  const primary = existing[0] as any;
  const extraDeleteIds = collectDeleteIds(existing.slice(1));

  const merged = mergeEffectiveRule(
    primary,
    definedRules[0],
    dateMode,
    timeMode,
  );
  if (!merged) {
    return emptyPayload();
  }

  if (primary?.liftAccessRuleId != null) {
    // The merge can land on exactly what the set already has (e.g. replacing a
    // value with itself) — don't write in that case. Compared against the
    // persisted rule: the question is whether this write would change the row.
    const alreadyMatches =
      areDatesEqual(primary.effectiveDateStart, merged.effectiveDateStart) &&
      areDatesEqual(primary.effectiveDateEnd, merged.effectiveDateEnd) &&
      areTimesEqual(
        primary.effectiveTimeSlotStart,
        merged.effectiveTimeSlotStart,
      ) &&
      areTimesEqual(primary.effectiveTimeSlotEnd, merged.effectiveTimeSlotEnd);

    if (alreadyMatches) {
      return { add: [], update: [], delete: extraDeleteIds };
    }

    return {
      add: [],
      update: [
        mapEffectiveDateRangeUpdate({
          ...merged,
          liftAccessRuleId: primary.liftAccessRuleId,
        }),
      ],
      delete: extraDeleteIds,
    };
  }

  // No existing rule to merge into. The merge is already known to be complete.
  return {
    add: [mapEffectiveDateRange(merged)],
    update: [],
    delete: extraDeleteIds,
  };
}

/**
 * Expands a single bulk edit intent into one AccessRuleChangeRequest per selected
 * rule set, reusing each rule set's own persisted liftAccessRuleIds so the existing
 * single-context /liftaccess/save-changes endpoint can apply them.
 */
export function mapAccessRuleBulkChangeRequests(
  params: MapAccessRuleBulkChangeParams,
): AccessRuleChangeRequest[] {
  const { userId, operatorId, ruleSets, changeSet, modes } = params;

  const definedEffective = changeSet.effectiveDateRange.created ?? [];
  const definedMaxDays = changeSet.maxDays.created ?? [];
  const definedBlackoutRange = changeSet.blackoutDateRange.created ?? [];
  const definedBlackoutDow = changeSet.blackoutDayOfWeek.created ?? [];

  // One stamp for the whole edit: with UserId it is what ties these requests
  // together in the audit log. Per rule set would give each its own.
  const requestUtcDateTime = getRequestUtcDateTime();

  return ruleSets.map((ruleSet) => ({
    RequestUtcDateTime: requestUtcDateTime,
    UserId: userId,
    OperatorId: operatorId,
    Context: toContext(ruleSet),
    EffectiveDateRangeRules: buildEffectiveCategory(
      modes.effectiveDate,
      modes.effectiveTime,
      definedEffective,
      ruleSet.effectiveDateRangeRules,
    ),
    MaxDaysRules: buildSingletonCategory(
      modes.maxDays,
      definedMaxDays,
      mapMaxDays,
      mapMaxDaysUpdate,
      ruleSet.maxDaysRules,
      (defined, existing) =>
        (defined?.numberOfDays ?? null) === (existing?.numberOfDays ?? null) &&
        !!defined?.sharedUsage === !!(existing as any)?.sharedUsage,
    ),
    BlackoutDateRangeRules: buildListCategory(
      modes.blackoutDateRange,
      definedBlackoutRange,
      mapBlackoutDateRange,
      ruleSet.blackoutDateRangeRules,
      blackoutDateRangeDiffAdapter.getIdentityKey,
    ),
    BlackoutDayOfWeekRules: buildListCategory(
      modes.blackoutDayOfWeek,
      definedBlackoutDow,
      mapBlackoutDayOfWeek,
      ruleSet.blackoutDayOfWeekRules,
      blackoutDayOfWeekDiffAdapter.getIdentityKey,
    ),
  })) as AccessRuleChangeRequest[];
}
